import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
  SortingState,
  ColumnFiltersState,
  GroupingState,
  ExpandedState,
  ColumnDef,
} from '@tanstack/react-table';
import type { Row } from '../api';
import { fmt } from '../lib/format';

export type TableState = {
  sorting: SortingState;
  filters: ColumnFiltersState;
  grouping: GroupingState;
  expanded: ExpandedState;
  globalFilter: string;
};

type Props = {
  rows: Row[];
  state: TableState;
  setState: (updater: (s: TableState) => TableState) => void;
  visibleColumns: string[];
  onTableInstance?: (t: ReturnType<typeof useReactTable<Row>>) => void;
};

export function buildColumns(visible: string[]): ColumnDef<Row>[] {
  const all: Record<string, ColumnDef<Row>> = {
    date_start: {
      id: 'date_start',
      header: 'Data',
      accessorKey: 'date_start',
      enableGrouping: false,
      cell: (info) => <span className="num">{info.getValue<string>() || '—'}</span>,
      aggregatedCell: () => <span className="num" style={{ color: 'var(--muted)' }}>—</span>,
    },
    account_name: {
      id: 'account_name',
      header: 'Conta',
      accessorKey: 'account_name',
      enableGrouping: true,
      cell: (info) => info.getValue<string>() || '—',
    },
    campaign_name: {
      id: 'campaign_name',
      header: 'Campanha',
      accessorKey: 'campaign_name',
      enableGrouping: true,
      cell: (info) => info.getValue<string>() || '—',
    },
    adset_name: {
      id: 'adset_name',
      header: 'Ad Set',
      accessorKey: 'adset_name',
      enableGrouping: true,
      cell: (info) => info.getValue<string>() || '—',
    },
    ad_name: {
      id: 'ad_name',
      header: 'Anúncio',
      accessorKey: 'ad_name',
      enableGrouping: true,
      cell: (info) => info.getValue<string>() || '—',
    },
    creative_name: {
      id: 'creative_name',
      header: 'Criativo',
      accessorKey: 'creative_name',
      enableGrouping: true,
      cell: ({ row }) => {
        const thumb = row.original.creative_thumbnail;
        const name = row.original.creative_name || row.original.creative_id || '—';
        return (
          <span>
            {thumb && <img src={thumb} className="thumb" alt="" />}
            {name}
          </span>
        );
      },
    },
    utm_source: { id: 'utm_source', header: 'utm_source', accessorKey: 'utm_source', enableGrouping: true },
    utm_medium: { id: 'utm_medium', header: 'utm_medium', accessorKey: 'utm_medium', enableGrouping: true },
    utm_campaign: { id: 'utm_campaign', header: 'utm_campaign', accessorKey: 'utm_campaign', enableGrouping: true },
    utm_content: { id: 'utm_content', header: 'utm_content', accessorKey: 'utm_content', enableGrouping: true },
    utm_term: { id: 'utm_term', header: 'utm_term', accessorKey: 'utm_term', enableGrouping: true },
    publisher_platform: {
      id: 'publisher_platform',
      header: 'Plataforma',
      accessorKey: 'publisher_platform',
      enableGrouping: true,
    },
    device_platform: { id: 'device_platform', header: 'Device', accessorKey: 'device_platform', enableGrouping: true },
    country: { id: 'country', header: 'País', accessorKey: 'country', enableGrouping: true },
    spend: {
      id: 'spend',
      header: 'Gasto',
      accessorKey: 'spend',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
    },
    impressions: {
      id: 'impressions',
      header: 'Impressões',
      accessorKey: 'impressions',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
    },
    clicks: {
      id: 'clicks',
      header: 'Cliques',
      accessorKey: 'clicks',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
    },
    link_clicks: {
      id: 'link_clicks',
      header: 'Link clicks',
      accessorKey: 'link_clicks',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
    },
    reach: {
      id: 'reach',
      header: 'Alcance',
      accessorKey: 'reach',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
    },
    ctr: {
      id: 'ctr',
      header: 'CTR',
      accessorKey: 'ctr',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.pct(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.pct(getValue<number>())}</span>,
    },
    cpc: {
      id: 'cpc',
      header: 'CPC',
      accessorKey: 'cpc',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
    },
    cpm: {
      id: 'cpm',
      header: 'CPM',
      accessorKey: 'cpm',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
    },
    conversions: {
      id: 'conversions',
      header: 'Conversões',
      accessorKey: 'conversions',
      aggregationFn: 'sum',
      cell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.num(getValue<number>())}</span>,
    },
    cpa: {
      id: 'cpa',
      header: 'CPA',
      accessorKey: 'cpa',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.brl(getValue<number>())}</span>,
    },
    roas: {
      id: 'roas',
      header: 'ROAS',
      accessorKey: 'roas',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.ratio(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.ratio(getValue<number>())}</span>,
    },
    conversion_rate: {
      id: 'conversion_rate',
      header: 'Conv. Rate',
      accessorKey: 'conversion_rate',
      aggregationFn: 'mean',
      cell: ({ getValue }) => <span className="num">{fmt.pct(getValue<number>())}</span>,
      aggregatedCell: ({ getValue }) => <span className="num">{fmt.pct(getValue<number>())}</span>,
    },
  };
  return visible.filter((v) => all[v]).map((v) => all[v]);
}

export function DataTable({ rows, state, setState, visibleColumns, onTableInstance }: Props) {
  const columns = useMemo(() => buildColumns(visibleColumns), [visibleColumns]);

  const table = useReactTable<Row>({
    data: rows,
    columns,
    state: {
      sorting: state.sorting,
      columnFilters: state.filters,
      grouping: state.grouping,
      expanded: state.expanded,
      globalFilter: state.globalFilter,
    },
    onSortingChange: (updater) =>
      setState((s) => ({ ...s, sorting: typeof updater === 'function' ? updater(s.sorting) : updater })),
    onColumnFiltersChange: (updater) =>
      setState((s) => ({ ...s, filters: typeof updater === 'function' ? updater(s.filters) : updater })),
    onGroupingChange: (updater) =>
      setState((s) => ({ ...s, grouping: typeof updater === 'function' ? updater(s.grouping) : updater })),
    onExpandedChange: (updater) =>
      setState((s) => ({ ...s, expanded: typeof updater === 'function' ? updater(s.expanded) : updater })),
    onGlobalFilterChange: (v) => setState((s) => ({ ...s, globalFilter: v })),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetExpanded: false,
    globalFilterFn: 'includesString',
  });

  if (onTableInstance) onTableInstance(table);

  return (
    <div className="table-wrap" data-testid="data-table">
      <table>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const canSort = h.column.getCanSort();
                const sort = h.column.getIsSorted();
                return (
                  <th key={h.id} data-column={h.column.id}>
                    <div
                      onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {sort && <span className="sort-ind">{sort === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                    {h.column.getCanFilter() && (
                      <input
                        value={(h.column.getFilterValue() ?? '') as string}
                        onChange={(e) => h.column.setFilterValue(e.target.value)}
                        placeholder="filtrar…"
                        data-testid={`filter-${h.column.id}`}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="empty">
                Sem dados pra este filtro.
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row) => {
            const isGrouped = row.getIsGrouped();
            return (
              <tr key={row.id} className={isGrouped ? 'grouped' : ''} data-testid="data-row">
                {row.getVisibleCells().map((cell, i) => (
                  <td key={cell.id}>
                    {isGrouped && i === 0 ? (
                      <span>
                        <button
                          className="expand-btn"
                          onClick={row.getToggleExpandedHandler()}
                          data-testid="expand-btn"
                        >
                          {row.getIsExpanded() ? '▼' : '▶'}
                        </button>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                        <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                          ({row.subRows.length})
                        </span>
                      </span>
                    ) : cell.getIsAggregated() ? (
                      flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext())
                    ) : cell.getIsPlaceholder() ? null : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
